"use strict";

exports.__esModule = true;
exports.default = exports.Input = void 0;
var _react = _interopRequireDefault(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _clsx = _interopRequireDefault(require("clsx"));
var _useFormElementValidation = _interopRequireDefault(require("../../hooks/useFormElementValidation"));
var _useRandomId = _interopRequireDefault(require("../../hooks/useRandomId"));
var _Icon = _interopRequireDefault(require("../Icon/Icon"));
var _Tooltip = _interopRequireDefault(require("../Tooltip/Tooltip"));
var _useLabel2 = _interopRequireDefault(require("../../hooks/useLabel"));
var _excluded = ["id", "name", "type", "value", "label", "placeholder", "helper", "floatLabel", "clearable", "readOnly", "disabled", "required", "onChange", "onInput", "onBlur", "onError", "className", "icon", "iconPosition", "addon", "addonPosition", "maxChar", "customValidation", "booleanValidation", "customMessage", "labelAria", "withErrorTooltip"];
function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
/**
 * The Input component allows an user to enter data into the application.
 */
var Input = exports.Input = function Input(props) {
  var controlledId = props.id,
    name = props.name,
    type = props.type,
    controlledValue = props.value,
    initialLabel = props.label,
    placeholder = props.placeholder,
    helper = props.helper,
    floatLabel = props.floatLabel,
    clearable = props.clearable,
    readOnly = props.readOnly,
    disabled = props.disabled,
    required = props.required,
    onChange = props.onChange,
    onInput = props.onInput,
    onBlur = props.onBlur,
    onError = props.onError,
    className = props.className,
    icon = props.icon,
    iconPosition = props.iconPosition,
    addon = props.addon,
    addonPosition = props.addonPosition,
    maxChar = props.maxChar,
    customValidation = props.customValidation,
    booleanValidation = props.booleanValidation,
    customMessage = props.customMessage,
    labelAria = props.labelAria,
    withErrorTooltip = props.withErrorTooltip,
    attributes = _objectWithoutPropertiesLoose(props, _excluded);
  var inputRef = _react.default.useRef(null);
  var id = _react.default.useRef((0, _useRandomId.default)(controlledId)).current;
  var _React$useState = _react.default.useState(controlledValue || ''),
    value = _React$useState[0],
    setValue = _React$useState[1];
  var _React$useState2 = _react.default.useState([0, 0]),
    caretPosition = _React$useState2[0],
    setCaretPosition = _React$useState2[1];
  var _useFormElementValida = (0, _useFormElementValidation.default)(value, {
      required: required,
      maxChar: maxChar,
      email: type === 'email',
      number: type === 'number',
      customValidation: customValidation,
      customMessage: customMessage,
      booleanValidation: booleanValidation
    }),
    errors = _useFormElementValida[0],
    methods = _useFormElementValida[1];
  var _useLabel = (0, _useLabel2.default)(initialLabel, _extends({}, props, {
      id: id,
      value: value
    })),
    label = _useLabel.label;
  _react.default.useEffect(function () {
    return setValue(controlledValue);
  }, [controlledValue]);
  _react.default.useEffect(function () {
    inputRef.current.value = value || '';
  }, [value]);
  _react.default.useEffect(function () {
    if (type === 'text') {
      inputRef.current.setSelectionRange(caretPosition[0], caretPosition[1]);
    }
  }, [caretPosition, type]);
  _react.default.useEffect(function () {
    if (typeof onError === 'function') {
      onError({
        name: inputRef.current.name,
        errors: errors.length ? errors : []
      });
    }
  }, [errors, onError]);
  var handleChange = function handleChange(event) {
    event.persist();
    var _event$target = event.target,
      value = _event$target.value,
      min = _event$target.min,
      max = _event$target.max;
    setValue(value);
    if (type === 'text') {
      setCaretPosition([event.target.selectionStart, event.target.selectionEnd]);
    }
    if (type === 'number') {
      value = Math.max(Number(min), Math.min(Number(max), Number(value)));
    }
    methods.onChange(value);
    if (typeof onChange === 'function') {
      onChange(inputRef.current.value || '', inputRef.current.name);
    }
  };
  var handleOnClear = function handleOnClear() {
    setValue('');
    methods.onChange('');
    if (typeof onChange === 'function') {
      onChange('', inputRef.current.name);
    }
  };
  var handleInput = function handleInput(event) {
    event.persist();
    if (type === 'text') {
      setCaretPosition([event.target.selectionStart, event.target.selectionEnd]);
    }
    methods.onInput(event);
    if (typeof onInput === 'function') {
      onInput(event);
    }
  };
  var handleBlur = function handleBlur(event) {
    methods.onBlur(event);
    if (typeof onBlur === 'function') {
      onBlur(event);
    }
  };
  var inputAddon = /*#__PURE__*/_react.default.isValidElement(addon) ? addon : /*#__PURE__*/_react.default.createElement('span', {}, addon);
  var containerClasses = (0, _clsx.default)('uk-form-item', 'uk-input', (clearable || (icon == null ? void 0 : icon.length)) && 'has-icon', (icon == null ? void 0 : icon.length) && "has-icon--" + iconPosition, addon && 'has-info', addon && "has-info--" + addonPosition, helper.length > 0 && 'has-helper', (errors.length > 0 || disabled || readOnly) && 'is-frozen', errors.length > 0 && 'has-error', errors.length > 0 && withErrorTooltip && 'has-error--tooltip', clearable && value && 'is-clearable', className);
  return /*#__PURE__*/_react.default.createElement("div", {
    className: containerClasses
  }, label, /*#__PURE__*/_react.default.createElement("div", {
    className: "flex-container",
    role: type === 'search' ? 'search' : undefined
  }, /*#__PURE__*/_react.default.createElement("div", {
    className: "uk-input__field"
  }, /*#__PURE__*/_react.default.createElement("input", _extends({
    className: (0, _clsx.default)('uk-inputtable', clearable && 'right'),
    ref: inputRef,
    type: type,
    id: id,
    name: name || id
    // defaultValue={controlledValue}
    // value={value || ''}
    ,
    placeholder: placeholder && !floatLabel ? placeholder : null,
    autoComplete: "off",
    disabled: disabled,
    readOnly: readOnly,
    required: required,
    onChange: handleChange,
    onInput: handleInput,
    onBlur: handleBlur,
    "aria-describedby": (name || id) + "-helper-text " + (name || id) + "-required-message",
    "aria-errormessage": (name || id) + "-errors-text",
    "aria-invalid": errors.length > 0,
    tabIndex: readOnly ? '-1' : undefined
  }, attributes)), icon && icon.length && (!clearable || !value) && /*#__PURE__*/_react.default.createElement(_Icon.default, {
    className: (0, _clsx.default)('uk-input__icon', iconPosition),
    name: icon
  }), clearable && value && /*#__PURE__*/_react.default.createElement("div", {
    "aria-hidden": "true",
    className: "uk-input__clearButton " + iconPosition,
    onClick: handleOnClear
  }, "\xD7"), errors.length > 0 && withErrorTooltip && /*#__PURE__*/_react.default.createElement(_Tooltip.default, {
    trigger: "!",
    className: "uk-form-item__error-tooltip"
  }, /*#__PURE__*/_react.default.createElement("div", {
    id: (name || id) + "-errors-text"
  }, errors))), addon && /*#__PURE__*/_react.default.createElement("div", {
    className: (0, _clsx.default)('uk-inputtable__addon', addonPosition)
  }, inputAddon)), errors.length > 0 && !withErrorTooltip && /*#__PURE__*/_react.default.createElement("span", {
    className: "uk-form-item__error",
    id: (name || id) + "-errors-text"
  }, errors), errors.length > 0 && !withErrorTooltip ? '' : helper.length > 0 && /*#__PURE__*/_react.default.createElement("small", {
    className: "uk-form-item__helper",
    id: (name || id) + "-helper-text"
  }, helper));
};
Input.propTypes = {
  /**
   * ID of the Input (used to bind the label to the Input)
   * NB: the ID will be randomly generated if not set.
   */
  id: _propTypes.default.string,
  /**
   * Name of the Input (used instead of the ID when submitted)
   */
  name: _propTypes.default.string,
  /**
   * Type of the Input
   */
  type: _propTypes.default.oneOf(['email', 'number', 'password', 'search', 'tel', 'text']),
  /**
   * Default value of the Input
   * Form-validation: set validation rules depending of type
   */
  value: _propTypes.default.oneOfType([_propTypes.default.string, _propTypes.default.number]),
  /**
   * Label of the Input
   */
  label: _propTypes.default.oneOfType([_propTypes.default.string, _propTypes.default.element]),
  /**
   * Input placeholder
   */
  placeholder: _propTypes.default.string,
  /**
   * Helper text, is replaced by a message in case of error
   */
  helper: _propTypes.default.string,
  /**
   * Remove the placeholder (if any), replace it with a floating label
   */
  floatLabel: _propTypes.default.bool,
  /**
   * Add a button to clear the Input
   */
  clearable: _propTypes.default.bool,
  /**
   * Set the Input as read-only
   */
  readOnly: _propTypes.default.bool,
  /**
   * Set the Input as disabled
   */
  disabled: _propTypes.default.bool,
  /**
   * Form-validation: set the Input as required
   */
  required: _propTypes.default.bool,
  /**
   * Callback function onChange
   */
  onChange: _propTypes.default.func,
  /**
   * Callback function onInput
   */
  onInput: _propTypes.default.func,
  /**
   * Callback function onBlur
   */
  onBlur: _propTypes.default.func,
  /**
   * Callback function onError
   */
  onError: _propTypes.default.func,
  /**
   * Add an icon
   */
  icon: _propTypes.default.string,
  /**
   * Position the icon relative to the input element
   */
  iconPosition: _propTypes.default.oneOf(['left', 'right']),
  /**
   * Add an informative box next to the input element
   */
  addon: _propTypes.default.oneOfType([_propTypes.default.string, _propTypes.default.element]),
  /**
   * Position the info relative to the input element
   */
  addonPosition: _propTypes.default.oneOf(['left', 'right']),
  maxChar: _propTypes.default.number,
  /**
   * Add a custom validation with a regex
   */
  customValidation: _propTypes.default.objectOf(_propTypes.default.instanceOf(RegExp)),
  /**
   * Add a custom message when custom validation is used
   */
  customMessage: _propTypes.default.object,
  /**
   * Add a custom boolean validation
   */
  booleanValidation: _propTypes.default.bool,
  /**
   * Provide text to labelAria only if props label is omitted.
   */
  labelAria: _propTypes.default.string,
  /**
   * Display error in tooltip otherwise display error below input.
   */
  withErrorTooltip: _propTypes.default.bool
};
Input.defaultProps = {
  type: 'text',
  placeholder: '',
  helper: '',
  floatLabel: false,
  readOnly: false,
  clearable: false,
  disabled: false,
  required: false,
  icon: '',
  iconPosition: 'right',
  addonPosition: 'right',
  withErrorTooltip: true
};
var _default = exports.default = Input;